/* global Excel, Word, console, Office */

// ── Snapshot reader ────────────────────────────────────────────────────────

export async function readSnapshot(host) {
  try {
    const h = host?.toString() ?? "";
    if (h.includes("Excel")) return await readExcelSnapshot();
    if (h.includes("Word")) return await readWordSnapshot();
    if (h.includes("PowerPoint")) return await readPowerPointSnapshot();
    return null;
  } catch (err) {
    console.warn("[AIPilot] Snapshot error:", err);
    return null;
  }
}

async function readExcelSnapshot() {
  return await Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    const snapshot = { sheets: [] };

    for (const ws of sheets.items) {
      const used = ws.getUsedRangeOrNullObject();
      used.load("isNullObject");
      await context.sync();

      if (used.isNullObject) {
        snapshot.sheets.push({ name: ws.name, usedRange: null, data: [] });
        continue;
      }

      used.load(["address", "values", "rowCount", "columnCount"]);
      await context.sync();

      const values = used.rowCount > 100 ? used.values.slice(0, 100) : used.values;
      snapshot.sheets.push({
        name: ws.name,
        usedRange: used.address,
        rowCount: used.rowCount,
        columnCount: used.columnCount,
        data: values,
        truncated: used.rowCount > 100,
      });
    }

    const activeSheet = context.workbook.worksheets.getActiveWorksheet();
    activeSheet.load("name");
    await context.sync();
    snapshot.activeSheet = activeSheet.name;

    return snapshot;
  });
}

async function readWordSnapshot() {
  return await Word.run(async (context) => {
    context.document.body.load("text");
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style");
    await context.sync();

    return {
      text: context.document.body.text.slice(0, 3000),
      paragraphs: paragraphs.items.slice(0, 50).map((p) => ({
        text: p.text,
        style: p.style,
      })),
    };
  });
}

// ── Tool dispatcher ────────────────────────────────────────────────────────

export async function executeTool(toolName, toolInput, host) {
  try {
    const h = host?.toString() ?? "";
    if (h.includes("Excel")) return await executeExcelTool(toolName, toolInput);
    if (h.includes("Word")) return await executeWordTool(toolName, toolInput);
    if (h.includes("PowerPoint")) return await executePowerPointTool(toolName, toolInput);
    return { success: false, error: `Host not supported: ${h}` };
  } catch (err) {
    console.error("[AIPilot] Tool error:", toolName, err);
    const msg = err?.debugInfo?.message ?? err?.message ?? String(err);
    return { success: false, error: msg };
  }
}

// ── Excel tools ────────────────────────────────────────────────────────────

async function executeExcelTool(toolName, input) {
  switch (toolName) {
    case "read_range": return await excelReadRange(input);
    case "write_range": return await excelWriteRange(input);
    case "write_formulas": return await excelWriteFormulas(input);
    case "format_range": return await excelFormatRange(input);
    case "create_table": return await excelCreateTable(input);
    case "create_sheet": return await excelCreateSheet(input);
    case "delete_sheet": return await excelDeleteSheet(input);
    case "clear_range": return await excelClearRange(input);
    case "insert_rows": return await excelInsertRows(input);
    case "add_chart": return await excelAddChart(input);
    case "freeze_panes": return await excelFreezePanes(input);
    case "rename_sheet": return await excelRenameSheet(input);
    case "sort_range": return await excelSortRange(input);
    case "add_hyperlink": return await excelAddHyperlink(input);
    default: return { success: false, error: `Unknown Excel tool: ${toolName}` };
  }
}

async function excelReadRange({ sheet, address }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    const range = ws.getRange(address);
    range.load(["values", "rowCount", "columnCount"]);
    await context.sync();
    return { success: true, data: { values: range.values, rows: range.rowCount, columns: range.columnCount } };
  });
}

async function excelWriteRange({ sheet, address, values }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    ws.getRange(address).values = values;
    await context.sync();
    return { success: true };
  });
}

async function excelWriteFormulas({ sheet, address, formulas }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    ws.getRange(address).formulas = formulas;
    await context.sync();
    return { success: true };
  });
}

async function excelFormatRange({ sheet, address, format }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    const range = ws.getRange(address);
    const fmt = range.format;

    if (format.bold !== undefined) fmt.font.bold = format.bold;
    if (format.italic !== undefined) fmt.font.italic = format.italic;
    if (format.font_size) fmt.font.size = format.font_size;
    if (format.font_color) fmt.font.color = format.font_color;
    if (format.fill_color) fmt.fill.color = format.fill_color;
    if (format.horizontal_alignment) fmt.horizontalAlignment = format.horizontal_alignment;
    if (format.vertical_alignment) fmt.verticalAlignment = format.vertical_alignment;
    if (format.wrap_text !== undefined) fmt.wrapText = format.wrap_text;
    if (format.column_width) fmt.columnWidth = format.column_width;
    if (format.row_height) fmt.rowHeight = format.row_height;
    if (format.number_format) range.numberFormat = [[format.number_format]];
    if (format.autofit_columns) fmt.autofitColumns();

    if (format.borders) {
      const sides = ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight", "InsideHorizontal", "InsideVertical"];
      sides.forEach((side) => {
        try { fmt.borders.getItem(side).style = format.borders; } catch (_) {}
      });
    }

    await context.sync();
    return { success: true };
  });
}

async function excelCreateTable({ sheet, address, name, style, show_totals }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);

    // Remove existing tables in the worksheet to avoid overlap
    ws.tables.load("items/name");
    await context.sync();
    for (const t of ws.tables.items) { t.delete(); }
    await context.sync();

    const table = ws.tables.add(address, true);
    if (name) table.name = name;
    if (style) table.style = style;
    if (show_totals) table.showTotals = show_totals;
    await context.sync();
    return { success: true };
  });
}

async function excelCreateSheet({ name, position }) {
  return Excel.run(async (context) => {
    // Delete if already exists
    const existing = context.workbook.worksheets.getItemOrNullObject(name);
    existing.load("isNullObject");
    await context.sync();
    if (!existing.isNullObject) {
      existing.delete();
      await context.sync();
    }

    const ws = context.workbook.worksheets.add(name);
    if (position !== undefined) ws.position = position;
    await context.sync();
    return { success: true };
  });
}

async function excelDeleteSheet({ name }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItemOrNullObject(name);
    ws.load("isNullObject");
    await context.sync();
    if (!ws.isNullObject) {
      ws.delete();
      await context.sync();
    }
    return { success: true };
  });
}

async function excelClearRange({ sheet, address }) {
  return Excel.run(async (context) => {
    context.workbook.worksheets.getItem(sheet).getRange(address).clear();
    await context.sync();
    return { success: true };
  });
}

async function excelInsertRows({ sheet, after_row, count }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    // Get the row range and insert below it
    const row = ws.getRange(`${after_row + 1}:${after_row + count}`);
    row.insert(Excel.InsertShiftDirection.down);
    await context.sync();
    return { success: true };
  });
}

async function excelAddChart({ sheet, chart_type, data_address, title, top, left, width, height }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    const range = ws.getRange(data_address);
    const chartTypeMap = {
      ColumnClustered: Excel.ChartType.columnClustered,
      Line: Excel.ChartType.line,
      Pie: Excel.ChartType.pie,
      Bar: Excel.ChartType.barClustered,
      Area: Excel.ChartType.area,
      XYScatter: Excel.ChartType.xyscatter,
    };
    const type = chartTypeMap[chart_type] ?? Excel.ChartType.columnClustered;
    const chart = ws.charts.add(type, range, Excel.ChartSeriesBy.auto);
    if (title) chart.title.text = title;
    if (top !== undefined) chart.top = top;
    if (left !== undefined) chart.left = left;
    if (width !== undefined) chart.width = width;
    if (height !== undefined) chart.height = height;
    await context.sync();
    return { success: true };
  });
}

async function excelFreezePanes({ sheet, rows, columns }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(sheet);
    if (rows && columns) {
      ws.freezePanes.freezeAt(ws.getRange(`${String.fromCharCode(65 + columns)}${rows + 1}`));
    } else if (rows) {
      ws.freezePanes.freezeRows(rows);
    } else if (columns) {
      ws.freezePanes.freezeColumns(columns);
    }
    await context.sync();
    return { success: true };
  });
}

async function excelRenameSheet({ current_name, new_name }) {
  return Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(current_name);
    ws.name = new_name;
    await context.sync();
    return { success: true };
  });
}

async function excelSortRange({ sheet, address, key_column_index, ascending }) {
  return Excel.run(async (context) => {
    const range = context.workbook.worksheets.getItem(sheet).getRange(address);
    range.sort.apply([{ key: key_column_index ?? 0, ascending: ascending ?? true }]);
    await context.sync();
    return { success: true };
  });
}

async function excelAddHyperlink({ sheet, address, display_text, target_sheet, target_address }) {
  return Excel.run(async (context) => {
    const range = context.workbook.worksheets.getItem(sheet).getRange(address);
    range.hyperlink = {
      textToDisplay: display_text,
      subAddress: `${target_sheet}!${target_address ?? "A1"}`,
    };
    await context.sync();
    return { success: true };
  });
}

// ── Word tools ─────────────────────────────────────────────────────────────

async function executeWordTool(toolName, input) {
  switch (toolName) {
    case "read_content": return await wordReadContent();
    case "insert_text": return await wordInsertText(input);
    case "replace_text": return await wordReplaceText(input);
    case "insert_heading": return await wordInsertHeading(input);
    case "insert_table": return await wordInsertTable(input);
    case "format_paragraph": return await wordFormatParagraph(input);
    case "insert_page_break": return await wordInsertPageBreak(input);
    case "set_document_title": return await wordSetDocumentTitle(input);
    case "insert_list": return await wordInsertList(input);
    case "clear_document": return await wordClearDocument();
    default: return { success: false, error: `Unknown Word tool: ${toolName}` };
  }
}

async function wordReadContent() {
  return Word.run(async (context) => {
    context.document.body.load("text");
    await context.sync();
    return { success: true, data: { text: context.document.body.text.slice(0, 5000) } };
  });
}

async function wordInsertText({ text, location }) {
  return Word.run(async (context) => {
    const loc = location === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
    context.document.body.insertText(text, loc);
    await context.sync();
    return { success: true };
  });
}

async function wordReplaceText({ search, replacement, match_case }) {
  return Word.run(async (context) => {
    const results = context.document.body.search(search, { matchCase: match_case ?? false });
    results.load("items");
    await context.sync();
    results.items.forEach((item) => item.insertText(replacement, Word.InsertLocation.replace));
    await context.sync();
    return { success: true, data: { replacements: results.items.length } };
  });
}

async function wordInsertHeading({ text, level, location }) {
  return Word.run(async (context) => {
    const loc = location === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
    const para = context.document.body.insertParagraph(text, loc);
    para.styleBuiltIn = `Heading${level ?? 1}`;
    await context.sync();
    return { success: true };
  });
}

async function wordInsertTable({ data, location, style }) {
  return Word.run(async (context) => {
    const rows = data.length;
    const cols = data[0]?.length ?? 0;
    const loc = location === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
    const table = context.document.body.insertTable(rows, cols, loc, data);
    table.styleBuiltIn = style ?? Word.Style.gridTable4Accent1;
    await context.sync();
    return { success: true };
  });
}

async function wordFormatParagraph({ search, bold, italic, font_size, font_color, alignment, highlight_color }) {
  return Word.run(async (context) => {
    const results = context.document.body.search(search, { matchCase: false });
    results.load("items");
    await context.sync();
    results.items.forEach((item) => {
      if (bold !== undefined) item.font.bold = bold;
      if (italic !== undefined) item.font.italic = italic;
      if (font_size) item.font.size = font_size;
      if (font_color) item.font.color = font_color;
      if (highlight_color) item.font.highlightColor = highlight_color;
      if (alignment) item.paragraphs.load("items");
    });
    await context.sync();
    return { success: true, data: { matched: results.items.length } };
  });
}

async function wordInsertPageBreak({ location }) {
  return Word.run(async (context) => {
    const loc = location === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
    context.document.body.insertBreak(Word.BreakType.page, loc);
    await context.sync();
    return { success: true };
  });
}

async function wordSetDocumentTitle({ title }) {
  return Word.run(async (context) => {
    context.document.properties.load("title");
    await context.sync();
    context.document.properties.title = title;
    await context.sync();
    return { success: true };
  });
}

async function wordInsertList({ items, list_type, location }) {
  return Word.run(async (context) => {
    const loc = location === "start" ? Word.InsertLocation.start : Word.InsertLocation.end;
    const isBulleted = list_type !== "numbered";
    for (const item of items) {
      const para = context.document.body.insertParagraph(item, Word.InsertLocation.end);
      para.listItem.level = 0;
      if (isBulleted) {
        para.listItem.listString = "•";
      }
    }
    await context.sync();
    return { success: true };
  });
}

async function wordClearDocument() {
  return Word.run(async (context) => {
    context.document.body.clear();
    await context.sync();
    return { success: true };
  });
}

// ── PowerPoint tools ───────────────────────────────────────────────────────
// NOTE: PowerPoint JS API coordinates are in points. A standard slide is
// 960x540 pts (widescreen). Keep all shapes within those bounds.

export async function readPowerPointSnapshot() {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id,items/shapes/items/name,items/shapes/items/textFrame/textRange/text");
    await context.sync();
    return {
      slideCount: slides.items.length,
      slideWidth: 960,
      slideHeight: 540,
      slides: slides.items.map((s, i) => ({
        index: i,
        id: s.id,
        shapes: s.shapes.items.map(sh => ({
          name: sh.name,
          text: sh.textFrame?.textRange?.text ?? "",
        })),
      })),
    };
  });
}

async function executePowerPointTool(toolName, input) {
  switch (toolName) {
    case "add_slide": return await pptAddSlide(input);
    case "delete_slide": return await pptDeleteSlide(input);
    case "set_slide_content": return await pptSetSlideContent(input);
    case "add_text_box": return await pptAddTextBox(input);
    case "set_background_color": return await pptSetBackgroundColor(input);
    default: return { success: false, error: `Unknown PowerPoint tool: ${toolName}` };
  }
}

async function pptAddSlide() {
  return PowerPoint.run(async (context) => {
    context.presentation.slides.add();
    await context.sync();
    return { success: true };
  });
}

async function pptDeleteSlide({ slide_index }) {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    if (slide_index >= 0 && slide_index < slides.items.length) {
      slides.items[slide_index].delete();
      await context.sync();
    }
    return { success: true };
  });
}

async function pptSetSlideContent({ slide_index, title, body }) {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/shapes/items/name,items/shapes/items/textFrame/textRange/text,items/shapes/items/type");
    await context.sync();
    const slide = slides.items[slide_index];
    if (!slide) return { success: false, error: `Slide ${slide_index} not found` };

    let titleSet = false;
    let bodySet = false;

    for (const shape of slide.shapes.items) {
      const name = (shape.name ?? "").toLowerCase();
      // Title placeholder: shape named "Title" or index 0 text frame
      if (!titleSet && title !== undefined && (name.includes("title") || name.includes("título") || name === "title 1")) {
        shape.textFrame.textRange.text = title;
        titleSet = true;
      } else if (!bodySet && body !== undefined && (
        name.includes("content") || name.includes("body") ||
        name.includes("contenido") || name.includes("text") ||
        name.includes("subtitle") || name.includes("placeholder")
      )) {
        shape.textFrame.textRange.text = body;
        bodySet = true;
      }
    }

    // If placeholders not found by name, use first two shapes
    if (!titleSet && title !== undefined && slide.shapes.items.length > 0) {
      slide.shapes.items[0].textFrame.textRange.text = title;
    }
    if (!bodySet && body !== undefined && slide.shapes.items.length > 1) {
      slide.shapes.items[1].textFrame.textRange.text = body;
    }

    await context.sync();
    return { success: true };
  });
}

async function pptAddTextBox({ slide_index, text, left, top, width, height, font_size, bold, color, fill_color }) {
  // Slide dimensions: 960x540 pts. Clamp to safe bounds.
  const safeLeft = Math.min(Math.max(left ?? 50, 0), 900);
  const safeTop = Math.min(Math.max(top ?? 50, 0), 490);
  const safeWidth = Math.min(width ?? 400, 960 - safeLeft);
  const safeHeight = Math.min(height ?? 80, 540 - safeTop);

  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    const slide = slides.items[slide_index];
    if (!slide) return { success: false, error: `Slide ${slide_index} not found` };

    const shape = slide.shapes.addTextBox(text, {
      left: safeLeft,
      top: safeTop,
      width: safeWidth,
      height: safeHeight,
    });
    shape.textFrame.autoSizeSetting = PowerPoint.ShapeAutoSize.autoSizeNone;
    shape.textFrame.wordWrap = true;
    if (font_size) shape.textFrame.textRange.font.size = font_size;
    if (bold !== undefined) shape.textFrame.textRange.font.bold = bold;
    if (color) shape.textFrame.textRange.font.color = color;
    if (fill_color && typeof shape.fill?.setSolidColor === "function") {
      shape.fill.setSolidColor(fill_color);
    }
    await context.sync();
    return { success: true };
  });
}

async function pptSetBackgroundColor({ slide_index, color }) {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    const slide = slides.items[slide_index];
    if (!slide) return { success: false, error: `Slide ${slide_index} not found` };
    // Load the background object before accessing it
    slide.load("background");
    await context.sync();
    if (typeof slide.background?.fill?.setSolidColor !== "function") {
      return { success: false, error: "set_background_color not supported in this Office host. Use add_text_box with fill_color to add colored shapes instead." };
    }
    slide.background.fill.setSolidColor(color);
    await context.sync();
    return { success: true };
  });
}
